/**
 * Role Form Page (Create/Edit)
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Alert,
    CircularProgress,
    FormControlLabel,
    Switch,
    Breadcrumbs,
    Link,
    Typography,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
    useGetRoleQuery,
    useCreateRoleMutation,
    useUpdateRoleMutation,
} from '../../store/api/rbacApi';
import { CreateRoleRequest, UpdateRoleRequest } from '../../types/rbac';
import { stripRetiredPermissionCodes } from '../../utils/rbacRetiredPermissions';
import { PermissionSelector } from './components/PermissionSelector';
import toast from 'react-hot-toast';

const RoleFormPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;

    const [formData, setFormData] = useState<CreateRoleRequest>({
        name: '',
        code: '',
        description: '',
        permission_codes: [],
        is_active: true,
        display_order: 0,
    });

    const { data: roleDetailData, isLoading: isLoadingDetail } = useGetRoleQuery(
        parseInt(id || '0'),
        {
            skip: !id,
        }
    );

    const [createRole, { isLoading: isCreating, error: createError }] = useCreateRoleMutation();
    const [updateRole, { isLoading: isUpdating, error: updateError }] = useUpdateRoleMutation();

    const isLoading = isCreating || isUpdating;
    const error = createError || updateError;

    useEffect(() => {
        if (roleDetailData?.data) {
            const roleDetail = roleDetailData.data;
            setFormData({
                name: roleDetail.name,
                code: roleDetail.code,
                description: roleDetail.description,
                permission_codes: stripRetiredPermissionCodes(roleDetail.all_permissions),
                is_active: roleDetail.is_active,
                display_order: roleDetail.display_order,
            });
        }
    }, [roleDetailData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isEdit && id) {
                const updateData: UpdateRoleRequest = {
                    name: formData.name,
                    description: formData.description,
                    permission_codes: stripRetiredPermissionCodes(formData.permission_codes),
                    is_active: formData.is_active,
                    display_order: formData.display_order,
                };
                await updateRole({ id: parseInt(id), ...updateData }).unwrap();
                toast.success(t('rbac.roles.toast.updateSuccess'));
            } else {
                await createRole({
                    ...formData,
                    permission_codes: stripRetiredPermissionCodes(formData.permission_codes),
                }).unwrap();
                toast.success(t('rbac.roles.toast.createSuccess'));
            }
            navigate('/roles');
        } catch (err: any) {
            console.error('Failed to save role:', err);
            toast.error(err?.data?.message || (isEdit ? t('rbac.roles.toast.updateError') : t('rbac.roles.toast.createError')));
        }
    };

    const handleChange = (field: keyof CreateRoleRequest, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // Auto-generate code from name
    const handleNameChange = (name: string) => {
        handleChange('name', name);
        if (!isEdit) {
            const code = name
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .replace(/\s+/g, '_');
            handleChange('code', code);
        }
    };

    // Format code to slug format
    const handleCodeChange = (code: string) => {
        const formattedCode = code
            .toLowerCase()
            .replace(/[^a-z0-9_-\s]/g, '') // Remove invalid characters
            .replace(/\s+/g, '_'); // Replace spaces with underscores
        handleChange('code', formattedCode);
    };

    if (isLoadingDetail) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    underline="hover"
                    color="inherit"
                    href="/roles"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/roles');
                    }}
                >
                    {t('nav.roles')}
                </Link>
                <Typography color="text.primary">
                    {isEdit ? t('rbac.roles.form.breadcrumbEdit') : t('rbac.roles.form.breadcrumbCreate')}
                </Typography>
            </Breadcrumbs>

            <PageHeader
                title={isEdit ? t('rbac.roles.form.editTitle') : t('rbac.roles.form.createTitle')}
                subtitle={isEdit ? t('rbac.roles.form.editSubtitle') : t('rbac.roles.form.createSubtitle')}
                action={
                    <Button
                        variant="outlined"
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/roles')}
                    >
                        {t('rbac.roles.backToList')}
                    </Button>
                }
            />

            <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {error && (
                        <Alert severity="error">
                            {(error as any)?.data?.message || t('rbac.roles.toast.saveError')}
                        </Alert>
                    )}

                    {/* Basic Information */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('rbac.roles.form.basicInfo')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                <TextField
                                    label={t('rbac.roles.form.name')}
                                    required
                                    fullWidth
                                    value={formData.name}
                                    onChange={(e) => handleNameChange(e.target.value)}
                                />

                                <TextField
                                    label={t('rbac.roles.form.code')}
                                    required
                                    fullWidth
                                    value={formData.code}
                                    onChange={(e) => handleCodeChange(e.target.value)}
                                    disabled={isEdit}
                                    helperText={isEdit ? t('rbac.roles.form.codeHelperEdit') : t('rbac.roles.form.codeHelper')}
                                    error={formData.code !== '' && !/^[a-z0-9_-]+$/.test(formData.code)}
                                />

                                <TextField
                                    label={t('rbac.roles.form.description')}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                />

                                <TextField
                                    label={t('rbac.roles.form.displayOrder')}
                                    type="number"
                                    fullWidth
                                    value={formData.display_order}
                                    onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
                                    helperText={t('rbac.roles.form.displayOrderHelper')}
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_active}
                                            onChange={(e) => handleChange('is_active', e.target.checked)}
                                        />
                                    }
                                    label={t('rbac.roles.form.active')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Permissions */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('rbac.roles.form.permissions')}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <PermissionSelector
                                    selectedPermissions={formData.permission_codes}
                                    onChange={(permissions) => handleChange('permission_codes', permissions)}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/roles')}
                            disabled={isLoading}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                            disabled={
                                isLoading ||
                                !formData.name ||
                                !formData.code ||
                                !/^[a-z0-9_-]+$/.test(formData.code)
                            }
                        >
                            {isLoading ? t('rbac.roles.form.saving') : isEdit ? t('rbac.roles.form.submitUpdate') : t('rbac.roles.form.submitCreate')}
                        </Button>
                    </Box>
                </Box>
            </form>
        </Box>
    );
};

export default RoleFormPage;

